import React, { useState } from 'react';
import { Webhook } from '../../types/webhook';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Trash2, Zap, Play, Loader2 } from 'lucide-react';
import { CreateWebhookDialog } from './CreateWebhookDialog';
import { testWebhook } from '../../services/webhookService';
import { useToast } from '../../hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';

interface WebhooksListProps {
  webhooks: Webhook[];
  onDelete: (id: string) => void;
  onUpdate: () => void;
  loading?: boolean;
}

export const WebhooksList: React.FC<WebhooksListProps> = ({
  webhooks,
  onDelete,
  onUpdate,
  loading = false,
}) => {
  const [testingWebhookId, setTestingWebhookId] = useState<string | null>(null);
  const { toast } = useToast();

  const handleTest = async (webhookId: string) => {
    try {
      setTestingWebhookId(webhookId);
      const result = await testWebhook(webhookId);

      if (result.success) {
        toast({
          title: 'Webhook Test Successful',
          description: `Received ${result.statusCode} response in ${result.responseTime}ms`,
        });
      } else {
        toast({
          title: 'Webhook Test Failed',
          description: result.error || 'Failed to deliver test webhook',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error testing webhook:', error);
      toast({
        title: 'Error',
        description: 'Failed to test webhook',
        variant: 'destructive',
      });
    } finally {
      setTestingWebhookId(null);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (webhooks.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-border rounded-lg">
        <Zap className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
        <p className="text-muted-foreground">No webhooks configured yet</p>
        <p className="text-sm text-muted-foreground mt-1">
          Create your first webhook to receive event notifications
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {webhooks.map((webhook) => (
        <div
          key={webhook.id}
          className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h4 className="font-semibold font-mono text-sm break-all">
                  {webhook.targetUrl}
                </h4>
              </div>

              <div className="flex flex-wrap gap-2 mb-3">
                {webhook.operations.map((op) => (
                  <Badge key={op} variant="secondary" className="text-xs">
                    {op}
                  </Badge>
                ))}
              </div>

              <div className="text-xs text-muted-foreground">
                <span>Created {formatDate(webhook.createdAt)}</span>
                {webhook.updatedAt.getTime() !== webhook.createdAt.getTime() && (
                  <span className="ml-3">
                    • Updated {formatDate(webhook.updatedAt)}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2 ml-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleTest(webhook.id)}
                disabled={loading || testingWebhookId === webhook.id}
              >
                {testingWebhookId === webhook.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
              </Button>

              <CreateWebhookDialog
                webhook={webhook}
                onSuccess={onUpdate}
                trigger={
                  <Button variant="outline" size="sm" disabled={loading}>
                    Edit
                  </Button>
                }
              />

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={loading}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Webhook?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete the webhook. You will no longer
                      receive event notifications at this URL. This action cannot be
                      undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onDelete(webhook.id)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete Webhook
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
